import { useState, useCallback, useRef } from 'react'
import { clsx } from 'clsx'
import styles from './DropZone.module.css'

interface AcceptedTypes {
  [mimeType: string]: string[]
}

interface DropZoneProps {
  onFilesSelected: (files: File[]) => void
  accept?: AcceptedTypes
  maxSize?: number
  disabled?: boolean
  multiple?: boolean
  label?: string
  dragLabel?: string
  compact?: boolean
}

export function DropZone({
  onFilesSelected,
  accept = {},
  maxSize = 4 * 1024 * 1024,
  disabled = false,
  multiple = false,
  label = 'Trascina qui la bolletta',
  dragLabel = 'Rilascia qui il file',
  compact = false
}: DropZoneProps) {
  const [isDragging, setIsDragging] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const acceptedMimeTypes = Object.keys(accept)
  const acceptedExtensions = Object.values(accept).flat()

  const validateFile = useCallback((file: File): string | null => {
    // Check file type
    if (acceptedMimeTypes.length > 0) {
      const isValidType = acceptedMimeTypes.some(type => file.type === type)
      if (!isValidType) {
        return `Tipo file non supportato. Usa: ${acceptedExtensions.join(', ')}`
      }
    }
    
    // Check file size
    if (file.size > maxSize) {
      const maxMB = Math.round(maxSize / 1024 / 1024)
      return `File troppo grande. Massimo ${maxMB}MB`
    }
    
    return null
  }, [acceptedMimeTypes, acceptedExtensions, maxSize])

  const handleFiles = useCallback((files: FileList | File[]) => {
    setError(null)
    
    const fileArray = Array.from(files)
    const validFiles: File[] = []
    
    for (const file of fileArray) {
      const validationError = validateFile(file)
      if (validationError) {
        setError(validationError)
        return
      }
      validFiles.push(file)
      if (!multiple) break
    }
    
    if (validFiles.length > 0) {
      onFilesSelected(validFiles)
    }
  }, [validateFile, onFilesSelected, multiple])

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (!disabled) {
      setIsDragging(true)
    }
  }, [disabled])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }, [])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
    
    if (disabled) return
    
    const files = e.dataTransfer.files
    if (files.length > 0) {
      handleFiles(files)
    }
  }, [disabled, handleFiles])

  const handleClick = useCallback(() => {
    if (!disabled && inputRef.current) {
      inputRef.current.click()
    }
  }, [disabled])

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (files && files.length > 0) {
      handleFiles(files)
    }
    // Reset input value to allow selecting same file again
    e.target.value = ''
  }, [handleFiles])

  const acceptString = acceptedExtensions.join(',')

  return (
    <div
      className={clsx(styles.dropzone, {
        [styles.dragging]: isDragging,
        [styles.disabled]: disabled,
        [styles.hasError]: error,
        [styles.compact]: compact
      })}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      onClick={handleClick}
      role="button"
      tabIndex={disabled ? -1 : 0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          handleClick()
        }
      }}
      aria-label="Trascina qui i file o clicca per selezionare"
    >
      <input
        ref={inputRef}
        type="file"
        accept={acceptString}
        multiple={multiple}
        onChange={handleInputChange}
        className={styles.hiddenInput}
        disabled={disabled}
      />
      
      {compact ? (
        <div className={styles.compactContent}>
          <i className="bi bi-upload" />
          <span>{isDragging ? dragLabel : label}</span>
        </div>
      ) : (
        <div className={styles.content}>
          <div className={styles.iconWrapper}>
            <i className="bi bi-cloud-arrow-up" />
          </div>
          
          <div className={styles.text}>
            <span className={styles.primary}>
              {isDragging ? dragLabel : label}
            </span>
            <span className={styles.secondary}>
              oppure <span className={styles.link}>sfoglia</span> dal dispositivo
            </span>
          </div>
          
          <div className={styles.formats}>
            <span className={styles.format}>PDF</span>
            <span className={styles.format}>PNG</span>
            <span className={styles.format}>JPG</span>
            <span className={styles.maxSize}>Max {Math.round(maxSize / 1024 / 1024)}MB</span>
          </div>
        </div>
      )}
      
      {error && (
        <div className={styles.error}>
          <i className="bi bi-exclamation-circle" />
          <span>{error}</span>
        </div>
      )}
    </div>
  )
}



